import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { params } from '@app/configurations';
import { isCustomerFull } from '@app/core/utilities/customer.utilities';
import { environment } from '@env/environment';
import { interval, Observable, Observer } from 'rxjs';
import { skipWhile } from 'rxjs/operators';
import { SignupAPI } from '../api/signup.service';
import { getQueryParametersAsString } from '../utilities/url.utilities';
import { IsSignupApp, UtilitiesService } from './utilities.service';

@Injectable()
export class ConnectAppGuardService {
  constructor(
    private router: Router,
    private utilitiesService: UtilitiesService,
    private signupAPI: SignupAPI,
  ) {}
  private _checkUserInfo(cb) {
    // router guards triggers before the app component finishes the initial userinfo request.
    // check for user info change for every 500ms and return
    if (!!this.signupAPI.userInfo) {
      return cb();
    }
    const sub$ = interval(500)
      .pipe(skipWhile(() => !this.signupAPI.userInfo))
      .subscribe(() => {
        cb();
        if (sub$) {
          sub$.unsubscribe();
        }
      });
  }
  canActivate(_: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    const data$: Observable<boolean> = new Observable((observer: Observer<boolean>) => {
      if (IsSignupApp()) {
        this._checkUserInfo(() => {
          // wait until user details are fetched
          // return observer.next(true); // dev
          if (window.__SIGNUP_IGNORE_GUARD) {
            // this flag is used for routes to bypass the below login and auto-redirection
            window.__SIGNUP_IGNORE_GUARD = false;
            return observer.next(true);
          }
          if (!this.signupAPI.store) {
            return this.signupAPI.getData().subscribe(
              () => {
                this.signupAPI.isNewAccountSignup = false;
                const value = this.utilitiesService.redirectConnectAppToCurrentState(
                  this.signupAPI.store,
                  state.url,
                  this.signupAPI.isCustomerSignup,
                );
                observer.next(value);
              },
              (err) => {
                if (err === 404) {
                  if (this.signupAPI.isCustomerSignup) {
                    // new user
                    const value = this.utilitiesService.redirectConnectAppToCurrentState(
                      this.signupAPI.store,
                      state.url,
                      true,
                    );
                    observer.next(value);
                  } else {
                    // new account signup
                    this.signupAPI.isNewAccountSignup = true;
                    this.signupAPI.store = {
                      status: environment.connectApp.status.OAUTH_CONSENT,
                      data: null,
                    };

                    const url = new URL(window.location.href);
                    const search = new URLSearchParams(url.search);
                    const customerId = search.get(params.CUSTOMER_ID);

                    this.signupAPI.getCustomerData(customerId).subscribe(({ customer, summary }) => {
                      if (isCustomerFull(customer, summary)) {
                        window.location.href = `/monitor?${params.CUSTOMER_ID}=${customerId}`;
                      } else {
                        const value = this.utilitiesService.redirectConnectAppToCurrentState(
                          this.signupAPI.store,
                          state.url,
                          false,
                        );
                        observer.next(value);
                      }
                    });
                  }
                } else {
                  // redirect to error page if it is an error
                  if (state.url !== '/error') {
                    setTimeout(() => {
                      window.__SIGNUP_IGNORE_GUARD = true;
                      this.router.navigate(['/error'], {
                        queryParamsHandling: 'merge',
                        replaceUrl: true,
                      });
                    });
                  }
                  observer.next(state.url === '/error');
                }
              },
              () => {},
            );
          } else {
            const value = this.utilitiesService.redirectConnectAppToCurrentState(
              this.signupAPI.store,
              state.url,
              this.signupAPI.isCustomerSignup,
            );
            observer.next(value);
          }
        });
      } else {
        this.router.navigateByUrl('/monitor?' + getQueryParametersAsString());
        observer.next(false);
      }
    });
    return data$;
  }
}
